import {FeatureGate, PropertyType, ReportName, ResourceType, SPECIAL_EVENTS} from 'common/report/constants';
import {Cohort as CohortType, EventDefinition, MpEvent, PropertyDefinition} from 'common/util/definitions-types';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {ContextPanelData, Metric} from 'common/widgets/context-panel/types';
import {getDefinitionForEvent, getDefinitionForProperty} from 'common/util/definitions';
import {formatMetricNumber} from 'common/util/index';
import {getReportUrl} from 'common/report/util';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {EntityTypeNameSingular} from 'common/report/util/saved-entities/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getShareCapability, isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {getTempLookupTableId} from 'common/report/stores/lookup-tables';
import {pluralize} from 'common/util/string';
import {getIconForPropertyType, getLabelForPropertyType, propertiesUtils} from 'common/report/util/properties';
import {renameEvent} from 'common/report/util/events';

import {isBehavioralCustomProperty, isPersistedCustomProperty} from '../custom-property-modal/util';

function isCustomPropertySaved(prop) {
  return !isNaN(prop.customPropertyId);
}

function getEventLink(event) {
  let pathBlock = ``;
  if (event.customEventId) {
    pathBlock = `#transformations/custom-events/$custom_event:${encodeURIComponent(event.customEventId)}`;
  } else {
    pathBlock = `#transformations/events/${encodeURIComponent(event.name)}`;
  }
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(`lexicon`, {projectId, workspaceId}) + pathBlock;
}

export function getPropertyLink(property, dataGroupName = null) {
  let pathBlock = ``;
  const propertyIdentifier = property.customPropertyId
    ? `$custom_property:${property.customPropertyId}`
    : property.name;

  if (property.resourceType === ResourceType.User) {
    const groupName = dataGroupName?.toLocaleLowerCase().replace(/\s/g, `-`);
    pathBlock = groupName
      ? `#transformations/profile-properties/${groupName}/${encodeURIComponent(propertyIdentifier)}`
      : `#transformations/profile-properties/user-profiles/${encodeURIComponent(propertyIdentifier)}`;
  } else {
    pathBlock = `#transformations/event-properties/${encodeURIComponent(propertyIdentifier)}`;
  }
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(`lexicon`, {projectId, workspaceId}) + pathBlock;
}

function getCohortLink(cohort: CohortType) {
  const cohortId = Number.isInteger(cohort.value) ? cohort.value : cohort.id;
  const url = Cohort.getExploreHashForUsersInCohort({
    cohortId: cohortId as number,
    dataGroupId: cohort.dataGroupId,
    cohortName: cohort.name,
  });
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(ReportName.Users, {projectId, workspaceId}) + `${url}`;
}

function shareEnabled() {
  return currentProjectStore.value?.getFeatureGate(FeatureGate.Sharing);
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTooltipDataForEvent(
  mpEvent: MpEvent,
  eventDefinitions: EventDefinition[],
  isRecentlyUsed: boolean,
  {allowEdit = true, allowDescriptionEdit = false, sampleEventUrl = ``} = {},
): ContextPanelData {
  const def = getDefinitionForEvent(mpEvent, eventDefinitions);
  if (!def) {
    return null;
  }

  const details = [];
  const metrics: Metric[] = [];
  const extraInfo = [];
  const links = [];
  const actions = [];

  let editItem = null;

  const header = renameEvent(def.name);

  const entityType = def.customEventId ? `Custom Event` : `Event`;
  const entityIcon = def.customEventId ? `custom` : `event`;

  if (allowDescriptionEdit) {
    const type = def.customEventId ? EntityTypeNameSingular.CustomEvent : EntityTypeNameSingular.Event;
    const id = def.customEventId ? def.customEventId : def.id;
    editItem = {id, type, name: def.name};
  }

  if (def.uiUniqueQueryCount) {
    const value = def.uiUniqueQueryCount;
    metrics.push({
      icon: `person`,
      tooltip: `Used by ${value} ${pluralize(`teammate`, value)} in the last 30 days`,
      value,
    });
  }

  if (def.uiQueryCount) {
    const value = def.uiQueryCount;
    const valueFormatted = formatMetricNumber(value);
    metrics.push({
      icon: `fire`,
      tooltip: `Queried ${valueFormatted} ${pluralize(`time`, value)} in the last 30 days`,
      value: valueFormatted,
    });
  }

  if (def.count) {
    const value = def.count;
    const valueFormatted = formatMetricNumber(value);
    metrics.push({
      icon: `pulse`,
      tooltip: `Tracked this event ${valueFormatted} ${pluralize(`time`, value)} in the last 30 days`,
      value: valueFormatted,
    });
  }

  if (!Object.values(SPECIAL_EVENTS).includes(def.name)) {
    actions.push({
      name: `viewSampleEvents`,
      label: `View Sample Events`,
      icon: `eye-on`,
      item: {
        id: def.id,
        name: def.name,
        url: sampleEventUrl,
      },
    });
  }

  if (allowEdit && def.customEventId) {
    // Allow context share button for custom events
    if (shareEnabled()) {
      actions.push({
        name: `shareCustomEvent`,
        label: `Share`,
        icon: `invite`,
        // Only superadmin or editor can share
        disabled: !getShareCapability(def).enableShare,
        disabledTooltipText: `You have view-only access and cannot share`,
        item: {
          id: def.customEventId || def.collectEverythingEventId,
          superadminOverrideShare: getShareCapability(def).requireSuperadminOverride,
        },
      });
    }

    const hasEditPerms = def.can_update_basic || isAllowedSuperadminOverride(def);

    actions.push({
      name: `editCustomEvent`,
      label: `Edit Custom Event`,
      icon: `compose`,
      item: {
        id: def.customEventId || def.collectEverythingEventId,
      },
      disabled: !hasEditPerms,
      disabledTooltipText: hasEditPerms ? `` : `You have view-only access and cannot edit`,
    });
  }

  if (def.platforms && def.platforms.length) {
    details.push({name: `Platforms`, value: def.platforms});
  }

  if (def.tags && def.tags.length) {
    details.push({name: `Tags`, value: def.tags});
  }

  if (def.isMixpanelDefinition) {
    extraInfo.push(`Automatically tracked by Mixpanel`);
  }

  if (def.collectEverythingEventId) {
    extraInfo.push(`Tracked through Codeless tracking`);
  }

  links.push({icon: `share`, href: getEventLink(def), tooltipText: `View in Lexicon`});

  return {
    header,
    entityType,
    entityIcon,
    trackedAs: def.displayName ? def.name : null,
    description: def.description,
    extraInfo,
    links,
    editItem,
    details,
    metrics,
    actions,
    isRecentlyUsed,
  };
}

// TODO opslead

type PropertyTypeOptions = {
  allowEdit: boolean;
  lookupTables: Obj;
  allowDescriptionEdit: boolean;
  iconReferenceType?: `resourceType` | `dataType`;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTooltipDataForProperty(
  {
    name,
    resourceType,
    dataType,
    path = [],
    customPropertyId = null,
    dataGroupId = null,
    dataGroupName = null,
    isLookupTableProperty = false,
    isRecentlyUsed = false,
  }: {
    name: string; // raw value of the property or formatted name of the lookup property
    resourceType: ResourceType;
    dataType: PropertyType;
    path?: string[];
    customPropertyId?: string;
    dataGroupId?: string;
    dataGroupName?: string;
    isLookupTableProperty?: boolean;
    isRecentlyUsed?: boolean;
  },
  propertyDefinitions: PropertyDefinition[],
  {allowEdit, lookupTables, allowDescriptionEdit, iconReferenceType}: PropertyTypeOptions = {
    allowEdit: true,
    lookupTables: {},
    allowDescriptionEdit: false,
    iconReferenceType: `resourceType`,
  },
): ContextPanelData {
  let def: PropertyDefinition;
  if (isLookupTableProperty) {
    def = {
      name,
      resourceType,
      description: ``,
      type: dataType,
      apiQueryCount: 0,
      uiQueryCount: 0,
    };
    allowDescriptionEdit = false;
  } else {
    def = getDefinitionForProperty({name, customPropertyId, dataGroupId}, propertyDefinitions);
  }

  if (!def) {
    return null;
  }

  const metrics: Metric[] = [];
  const details = [];
  const links = [];
  const actions = [];
  const header = propertiesUtils.renameProperty(def.name, def.resourceType);
  const subHeader = isLookupTableProperty ? `From ${lookupTables[dataGroupId].name}` : null;
  const extraInfo = [];

  let createdBy = null;
  if (def.customPropertyId && def.createdBy) {
    createdBy = def.createdBy;
  }

  let entityType = `Property`;
  let entityIcon = ``;

  const isCustomProperty = Boolean(def.customPropertyId);

  let propertyType = def.type;
  switch (iconReferenceType) {
    case `dataType`: {
      if (propertyType) {
        if (propertyType === `dimension`) {
          propertyType = def.customProperty?.joinPropertyType || def.join_property_type;
        }
        entityIcon = getIconForPropertyType(propertyType, {isCustom: isCustomProperty});
      }
      break;
    }
    case `resourceType`:
      entityIcon = def.resourceType === `user` ? `profile-attribute` : `event-attribute`;
      break;
  }
  entityType = getLabelForPropertyType(propertyType, {isCustom: isCustomProperty});
  if (isCustomProperty && isBehavioralCustomProperty(def.customProperty)) {
    entityType = `Behavior`;
    allowDescriptionEdit = false;
  }
  if (isCustomProperty && !isCustomPropertySaved(def)) {
    allowDescriptionEdit = false;
    extraInfo.push(`Local custom property, only visible in this report.`);
  }

  let editItem = null;
  if (allowDescriptionEdit && !def.customPropertyId) {
    const type =
      def.resourceType === `user` ? EntityTypeNameSingular.UserProperty : EntityTypeNameSingular.EventProperty;
    editItem = {id: def.id, type, name: def.name, dataGroupId};
  }

  if (def.uiUniqueQueryCount) {
    const value = def.uiUniqueQueryCount;
    metrics.push({
      icon: `person`,
      tooltip: `Used by ${value} ${pluralize(`teammate`, value)} in the last 30 days`,
      value,
    });
  }

  if (def.uiQueryCount || def.apiQueryCount) {
    const value = def.uiQueryCount + def.apiQueryCount;
    const valueFormatted = formatMetricNumber(value);
    metrics.push({
      icon: `fire`,
      tooltip: `Used by teammates ${valueFormatted} ${pluralize(`time`, value)} in the last 30 days`,
      value: valueFormatted,
    });
  }

  if (def.count) {
    const value = def.count;
    const valueFormatted = formatMetricNumber(value);
    const resourceLabel = def.resourceType === ResourceType.User ? `user` : `event`;
    metrics.push({
      icon: `pulse`,
      tooltip: `${valueFormatted} ${pluralize(resourceLabel, value)} with this property in the last 30 days`,
      value: valueFormatted,
    });
  }

  if (def.exampleValue) {
    details.push({name: `Example`, value: [def.exampleValue]});
  }

  const isUnsaved = def.customPropertyId && !isCustomPropertySaved(def);

  if (allowEdit && def.customPropertyId) {
    // TODO - scobb - initiate superadmin flow?
    // eslint-disable-next-line camelcase
    const hasPerms =
      !isPersistedCustomProperty(def.customPropertyId) ||
      def.customProperty.canUpdateBasic ||
      isAllowedSuperadminOverride(def.customProperty);

    const disabled = !hasPerms;
    const extraActionParams = {
      disabled,
      disabledTooltipText: hasPerms ? `` : `You have view-only access and cannot edit`,
    };
    if (allowDescriptionEdit && hasPerms) {
      editItem = {id: Number(def.customPropertyId), type: EntityTypeNameSingular.CustomProperty, name: def.name};
    }

    if (shareEnabled() && !isBehavioralCustomProperty(def.customProperty)) {
      actions.push({
        name: `shareCustomProperty`,
        label: `Share`,
        disabled: isUnsaved || !getShareCapability(def.customProperty).enableShare,
        disabledTooltipText: isUnsaved ? `Save custom property to share` : `You have view-only access and cannot share`,
        icon: `invite`,
        item: {
          property: {
            customPropertyId: def.customPropertyId,
            customProperty: def.customProperty,
          },
          superadminOverrideShare: getShareCapability(def.customProperty).requireSuperadminOverride,
        },
      });
    }

    actions.push({
      name: `editCustomProperty`,
      label: `Edit Property`,
      icon: `compose`,
      item: {
        property: {
          customPropertyId: def.customPropertyId,
          customProperty: def.customProperty,
        },
      },
      ...extraActionParams,
    });
  }

  if (def && (!def.customPropertyId || isCustomPropertySaved(def))) {
    links.push({icon: `share`, href: getPropertyLink(def, dataGroupName), tooltipText: `View in Lexicon`});
  }

  const tempDataGroupId = getTempLookupTableId({
    value: name,
    resourceType: def.resourceType,
    customPropertyId: Number(def.customPropertyId) || undefined,
  });
  let lookupTableId;
  let lookupTableIsTempMapping = false;
  if (tempDataGroupId) {
    lookupTableId = tempDataGroupId;
    lookupTableIsTempMapping = true;
  } else if (def.customProperty?.mappedDataGroupId) {
    lookupTableId = def.customProperty.mappedDataGroupId;
  } else if (def[`dimension_data_group_id`]) {
    lookupTableId = def[`dimension_data_group_id`];
  }

  // Ensure that lookupTableId is not referring to a group property
  // (group properties currently do not support Lookup table mappings so by definition they won't have a data group id that corresponds to a lookup table)
  if (lookupTableId && lookupTableId !== dataGroupId) {
    links.push({
      icon: `info`,
      href: `https://help.mixpanel.com/hc/en-us/articles/360044139291-Lookup-tables`,
      tooltipText: `More about Lookup Tables`,
    });
    details.push({
      name: `Mapped Lookup Table${lookupTableIsTempMapping ? ` (Temp)` : ``}`,
      value: [lookupTables?.[lookupTableId]?.name],
    });
  }

  return {
    header,
    subHeader,
    path,
    createdBy,
    isUnsaved,
    entityIcon,
    entityType,
    trackedAs: def.displayName ? def.name : null,
    description: def.description,
    editItem,
    metrics,
    details,
    links,
    actions,
    isRecentlyUsed,
    extraInfo,
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTooltipDataForCohort(cohort: CohortType, {allowDescriptionEdit = false} = {}): ContextPanelData {
  const isInlineCohort = !!(
    cohort.value &&
    typeof cohort.value !== `number` &&
    cohort.value.cohort &&
    cohort.value.cohort.raw_cohort
  );
  if (!cohort.name && !isInlineCohort) {
    return null;
  }

  const details = [];
  const metrics: Metric[] = [];
  const extraInfo = [];
  const actions = [];
  const links = [];

  if (!isInlineCohort) {
    links.push({icon: `share`, href: getCohortLink(cohort), tooltipText: `View on Users Page`});
    extraInfo.push(`Cohort ID: ${cohort.value}`);
  }
  let createdBy = null;
  if (cohort.createdBy) {
    createdBy = cohort.createdBy;
  }

  if (Number.isInteger(cohort.count)) {
    const descriptor = cohort.dataGroupId ? `unique` : `user`;
    const valueFormatted = cohort.count.toLocaleString();
    const countLabel = cohort.count === 1 ? `${valueFormatted} ${descriptor}` : `${valueFormatted} ${descriptor}s`;
    metrics.push({icon: `community`, tooltip: `${countLabel} in cohort`, value: valueFormatted});
  }

  let editItem = null;
  const canUpdateBasic = cohort.canUpdateBasic ?? cohort.can_update_basic ?? false;
  if (!isInlineCohort && canUpdateBasic && allowDescriptionEdit) {
    editItem = {id: cohort.value, type: `cohort`};
  }

  // inline cohort is not considered as a saved entity. A user should always be able to
  // edit an inline cohort but not be able to share.
  if (cohort.value) {
    // inline cohort is not considered as a saved entity thus not shareable
    if (shareEnabled()) {
      actions.push({
        name: `shareCohort`,
        icon: `invite`,
        label: `Share`,
        // Only superadmin or editor can share
        disabled: isInlineCohort || !(cohort.isSuperadmin || cohort.canShare),
        disabledTooltipText: isInlineCohort ? `Save cohort to share` : `You have view-only access and cannot share`,
        item: isInlineCohort
          ? {
              ...cohort,
              superadminOverrideShare: getShareCapability(cohort).requireSuperadminOverride,
            }
          : {
              id: cohort.value,
              dataGroupId: cohort.dataGroupId,
              superadminOverrideShare: getShareCapability(cohort).requireSuperadminOverride,
            },
      });
    }

    const hasEditPerms = isInlineCohort || canUpdateBasic || isAllowedSuperadminOverride(cohort);

    actions.push({
      name: `editCohort`,
      icon: hasEditPerms ? `compose` : `eye-on`,
      label: hasEditPerms ? `Edit Cohort` : `View Cohort`,
      disabled: false,
      item: isInlineCohort
        ? cohort
        : {
            id: cohort.value,
            dataGroupId: cohort.dataGroupId,
          },
    });
  }

  return {
    header: cohort.name,
    entityType: `Cohort`,
    entityIcon: `community`,
    createdBy,
    isUnsaved: isInlineCohort,
    description: cohort.description,
    editItem,
    metrics,
    details,
    extraInfo,
    links,
    actions,
  };
}
